import { useMemo } from 'react'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/** Frozen columns are scoped to the active saved view ('default' = no view). */
export const freezeScopeKey = (viewId: string | null | undefined): string =>
  viewId ? `view:${viewId}` : 'default'

type ColumnFreezeState = {
  pinnedByScope: Record<string, string[]>
  togglePin: (scope: string, columnId: string) => void
  setPinned: (scope: string, columnIds: string[], pinned: boolean) => void
  clearScope: (scope: string) => void
  replaceScope: (scope: string, columnIds: string[]) => void
  removeStalePinnedColumns: (scope: string, columnIds: string[]) => void
}

const EMPTY: string[] = []

// Writes a scope's list, dropping the bucket when empty.
function withScope(
  all: Record<string, string[]>,
  scope: string,
  ids: string[]
): Record<string, string[]> {
  const next = { ...all }
  if (ids.length > 0) next[scope] = ids
  else delete next[scope]
  return next
}

export const useColumnFreezeStore = create<ColumnFreezeState>()(
  persist(
    set => ({
      pinnedByScope: {},
      togglePin: (scope, columnId) =>
        set(state => {
          const current = state.pinnedByScope[scope] ?? EMPTY
          const ids = current.includes(columnId)
            ? current.filter(id => id !== columnId)
            : [...current, columnId]
          return { pinnedByScope: withScope(state.pinnedByScope, scope, ids) }
        }),
      setPinned: (scope, columnIds, pinned) =>
        set(state => {
          const current = state.pinnedByScope[scope] ?? EMPTY
          const targets = new Set(columnIds)
          const ids = pinned
            ? [...current, ...columnIds.filter(id => !current.includes(id))]
            : current.filter(id => !targets.has(id))
          if (ids.length === current.length) return state
          return { pinnedByScope: withScope(state.pinnedByScope, scope, ids) }
        }),
      clearScope: scope =>
        set(state =>
          scope in state.pinnedByScope
            ? { pinnedByScope: withScope(state.pinnedByScope, scope, []) }
            : state
        ),
      replaceScope: (scope, columnIds) =>
        set(state => {
          const current = state.pinnedByScope[scope] ?? EMPTY
          if (
            current.length === columnIds.length &&
            current.every((id, i) => id === columnIds[i])
          )
            return state
          return {
            pinnedByScope: withScope(state.pinnedByScope, scope, columnIds),
          }
        }),
      removeStalePinnedColumns: (scope, columnIds) =>
        set(state => {
          const current = state.pinnedByScope[scope] ?? EMPTY
          const valid = new Set(columnIds)
          const ids = current.filter(id => valid.has(id))
          if (ids.length === current.length) return state
          return { pinnedByScope: withScope(state.pinnedByScope, scope, ids) }
        }),
    }),
    {
      name: 'column-freeze',
      version: 1,
      // v0 stored a single global `pinnedColumnIds` array.
      migrate: persisted => {
        const p = (persisted ?? {}) as {
          pinnedColumnIds?: string[]
          pinnedByScope?: Record<string, string[]>
        }
        if (p.pinnedByScope) return { pinnedByScope: p.pinnedByScope }
        const legacy = p.pinnedColumnIds ?? []
        return { pinnedByScope: legacy.length ? { default: legacy } : {} }
      },
      partialize: state => ({ pinnedByScope: state.pinnedByScope }),
    }
  )
)

/** Pinned column ids for a scope; the Set is stable until the scope changes. */
export function usePinnedColumnIds(scope: string): Set<string> {
  const ids = useColumnFreezeStore(s => s.pinnedByScope[scope]) ?? EMPTY
  return useMemo(() => new Set(ids), [ids])
}
